/* ==========================================================================
   Admin General Custom Fields Page (PORTAL-164)

   Pharmacy-level custom fields that are NOT tied to a product. They render
   at the end of every New Prescription form (after the product sections),
   their answers are stored on each prescription, and they print on the fax
   under ADDITIONAL INFORMATION and as CUSTOM_<KEY> lines in the
   machine-readable zone — exactly like product custom fields. Typical use:
   an order-level attribute such as a brand or a partner order reference.

   PORTAL-181: each field can optionally be limited to specific providers
   (`provider_ids` on the definition, picked in the editor): null asks every
   provider, a list asks those providers, [] asks no one (parked, or its last
   assignee was deleted — the backend strips deleted providers from every
   assignment). The backend filters per provider.
   ========================================================================== */

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Save, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Alert } from '@/components/ui/Alert'
import { AdminSettingsSkeleton } from '@/components/skeletons'
import { CustomFieldsEditor } from '@/components/admin/CustomFieldsEditor'
import { getCustomFieldDefinitionProblems } from '@/utils/customFields'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import { getPharmacy, listProviders, updatePharmacy } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type { CustomFieldDefinition, ProviderResponse } from '@/api/types'

function sameDefinitions(a: CustomFieldDefinition[], b: CustomFieldDefinition[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Definitions saved before PORTAL-181 have no `provider_ids` key, while the
 * backend now echoes `provider_ids: null` on every field. Give the loaded
 * baseline the same shape, so picking and then clearing providers on a legacy
 * field does not read as a change.
 */
function normalizeDefinitions(fields: CustomFieldDefinition[]): CustomFieldDefinition[] {
  return fields.map((f) => ({ ...f, provider_ids: f.provider_ids ?? null }))
}

export function GeneralCustomFields() {
  const { pharmacy: authPharmacy } = useAdminAuth()
  const { success: showSuccess, error: showError } = useToast()
  const slug = authPharmacy?.portal_slug || ''

  const [fields, setFields] = useState<CustomFieldDefinition[]>([])
  // Baseline as last loaded/saved so Save is only enabled when something changed.
  const [savedFields, setSavedFields] = useState<CustomFieldDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState<string | undefined>()
  // PORTAL-181: provider list for the per-field assignment picker.
  const [providers, setProviders] = useState<ProviderResponse[]>([])
  const [providersLoaded, setProvidersLoaded] = useState(false)
  const [providersFailed, setProvidersFailed] = useState(false)

  const load = useCallback(async () => {
    if (!slug) return
    try {
      setLoading(true)
      const pharmacy = await getPharmacy(slug)
      const current = normalizeDefinitions(pharmacy.general_custom_fields ?? [])
      setFields(current)
      setSavedFields(current)
      setApiError(undefined)
    } catch (err) {
      setApiError(
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to load general custom fields. Please refresh the page.',
      )
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    void load()
  }, [load])

  // Load providers once for the assignment picker. Deliberately separate from
  // load(): a failure here must not blank the field list or block Save — the
  // picker simply stays hidden and existing assignments ride through untouched.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    listProviders(slug)
      .then((res) => {
        if (cancelled) return
        setProviders(res.providers)
        setProvidersLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setProvidersFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const problems = getCustomFieldDefinitionProblems(
    fields,
    providersLoaded ? new Set(providers.map((p) => p.id)) : undefined,
  )
  const dirty = !sameDefinitions(fields, savedFields)

  async function handleSave() {
    if (!slug || problems.length > 0) return
    try {
      setSaving(true)
      setApiError(undefined)
      const updated = await updatePharmacy(slug, { general_custom_fields: fields })
      const stored = normalizeDefinitions(updated.general_custom_fields ?? [])
      setFields(stored)
      setSavedFields(stored)
      showSuccess('General custom fields saved.')
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to save general custom fields. Please try again.'
      setApiError(message)
      showError(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <AdminSettingsSkeleton />
      </div>
    )
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">
              General Custom Fields
            </h1>
            <p className="text-gray-600 mt-1">
              Questions every prescription must answer, regardless of product.
            </p>
          </div>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={saving || !dirty || problems.length > 0}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" aria-hidden="true" />
                Save changes
              </>
            )}
          </Button>
        </div>

        {apiError && (
          <div className="mb-6">
            <Alert variant="error">{apiError}</Alert>
          </div>
        )}

        {providersFailed && (
          <div className="mb-6">
            <Alert variant="warning">
              Couldn&apos;t load your provider list. Fields can still be edited and
              saved, but provider assignments are hidden until you refresh the page.
            </Alert>
          </div>
        )}

        <div className="space-y-6">
          <Card>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-soft-lavender flex items-center justify-center shrink-0">
                <ClipboardList className="w-5 h-5 text-deep-purple" aria-hidden="true" />
              </div>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  General custom fields appear at the <strong>end of the New Prescription
                  page</strong>, after the product sections, and apply to every product
                  in the order. Use them for order-level details such as a brand,
                  a partner order reference, or a delivery note. Each field can be
                  limited to specific providers under <strong>Visible to</strong>, or
                  parked with <strong>No one</strong>; the default asks everyone.
                </p>
                <p>
                  Answers are stored with each prescription and printed on the fax under{' '}
                  <strong>Additional Information</strong> and in the machine-readable zone
                  as <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">CUSTOM_&lt;KEY&gt;</code>,
                  the same way product custom fields are. Field keys must therefore not
                  reuse a key already defined on a product in your{' '}
                  <Link to={`/rx/${slug}/admin/catalog`} className="text-deep-purple underline">
                    Product Catalog
                  </Link>
                  .
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <CustomFieldsEditor
              value={fields}
              onChange={(next) => {
                setFields(next)
                setApiError(undefined)
              }}
              disabled={saving}
              label="General Custom Fields"
              emptyMessage="No general custom fields defined. Add a field to ask it on every prescription."
              providers={providersLoaded ? providers : undefined}
            />
            {problems.length > 0 && (
              <ul className="mt-4 text-sm text-[var(--color-error)] list-disc list-inside">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}

export default GeneralCustomFields
